/**
 * The app's own date, time and number formatter.
 *
 * Every screen that paints a moment — the panel's clock, a `Last updated:` line,
 * a log row — used to hand the *browser* the job: `date.toLocaleDateString([], …)`
 * passes no locale, so the string follows the runtime's language instead of the
 * app's. On a panel set to Slovene that painted `Sun 20 Sept` beside `Nadzorne
 * plošče`; on a German machine an English UI painted `So., 20. Sept.`. Card
 * `t_82d404d4` found 97 such call sites in 75 files and this module is the one
 * place that decides what they format with.
 *
 * Same shape as `tt()` in `consoleText.ts`, and for the same reason: a formatted
 * date is copy, the reader's language is the locale store's, and reading the
 * store inside the call keeps the string reactive — switching language re-renders
 * the date with the rest of the surface.
 *
 * ── What the app's locale means for a format ────────────────────────────────
 *
 * The six catalogues are languages, not regions, so five of them are pinned to
 * the region the app writes for (`sl-SI`, `de-DE`, `es-ES`, `fr-FR`, `it-IT`).
 * `en` is the exception and deliberately so: an English UI must paint the same
 * date string it painted before this module existed, and "the same" means the
 * runtime's own English region — `en-GB` on the panel build this card is
 * verified against (`Sun 20 Sept`, a 24-hour clock), `en-US` on an American
 * machine (`Sun, Sep 20`, `02:34 PM`). Pinning `en` to either one would move
 * every English date on the other. A runtime that reports no English region at
 * all (a German browser running the app in English) gets `en-GB`, which is what
 * the app's own panels have always shown.
 *
 * A value the locale store cannot vouch for — it validates on load, this is
 * belt and braces — falls back to the English rule rather than to `undefined`,
 * because passing `undefined` is the bug this module exists to remove.
 */

import { useLocaleStore, type LocaleCode } from '../stores/locale';

/** The pinned tag per catalogue. `en` is handled by `englishTag()` below. */
const PINNED_TAGS: Record<Exclude<LocaleCode, 'en'>, string> = {
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  sl: 'sl-SI',
};

/** What an English UI formats with when the runtime reports no English region. */
const ENGLISH_FALLBACK = 'en-GB';

/** The runtime's own English region (`en-GB`, `en-US`), or null when it has none. */
function englishTag(): string | null {
  if (typeof navigator === 'undefined') return null;
  const reported = [...(navigator.languages ?? []), navigator.language];
  return reported.find((tag) => /^en-[A-Za-z]{2}$/.test(String(tag))) ?? null;
}

/** The BCP-47 tag the app formats with right now. */
export function localeTag(): string {
  const locale = useLocaleStore().currentLocale;
  if (locale === 'en' || !(locale in PINNED_TAGS)) return englishTag() ?? ENGLISH_FALLBACK;
  return PINNED_TAGS[locale as Exclude<LocaleCode, 'en'>];
}

/** Anything a date reaches these helpers as: a `Date`, an epoch number, an ISO string. */
export type DateInput = Date | number | string;

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/** A date, the way the reader's language writes one (`20. 9. 2026`). */
export function formatDate(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  return toDate(value).toLocaleDateString(localeTag(), options);
}

/** A time, the way the reader's language writes one (`14:34`). */
export function formatTime(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  return toDate(value).toLocaleTimeString(localeTag(), options);
}

/** Both at once — what `toLocaleString()` painted (`20. 9. 2026, 14:34`). */
export function formatDateTime(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  return toDate(value).toLocaleString(localeTag(), options);
}

/**
 * A number, the way the reader's language groups and separates one
 * (`1.234.567,89` in Slovene, `1,234,567.89` in English). `null`/`undefined` read
 * as the empty string, which is how the call sites' `stats.sent?.toLocaleString() || 0`
 * already treated a missing value.
 */
export function formatNumber(value: number | null | undefined, options?: Intl.NumberFormatOptions): string {
  return value == null ? '' : value.toLocaleString(localeTag(), options);
}
